import { events, ServerSentEventMessage } from 'fetch-event-stream';
import * as React from 'react';

interface MutationOptions<TVariables = unknown> {
	mutationFn: (variables: TVariables) => Promise<Response>;
	onError?: (error: Error, variables: TVariables) => void;
	onEvent: (event: ServerSentEventMessage, variables: TVariables) => void;
	onSuccess?: (params: TVariables) => void;
}

export function useStreamingResponseMutation<TVariables = unknown>({
	mutationFn,
	onError,
	onEvent,
	onSuccess,
}: MutationOptions<TVariables>) {
	const [status, setStatus] = React.useState<'idle' | 'loading' | 'streaming' | 'error'>('idle');
	const [error, setError] = React.useState<Error | null>(null);
	const abortControllerRef = React.useRef<AbortController | null>(null);
	const callbacksRef = React.useRef({
		mutationFn,
		onError,
		onEvent,
		onSuccess,
	});
	React.useEffect(() => {
		callbacksRef.current = {
			mutationFn,
			onError,
			onEvent,
			onSuccess,
		};
	}, [mutationFn, onError, onEvent, onSuccess]);
	const mutate = React.useCallback(async (params: TVariables) => {
		setStatus('loading');
		setError(null);
		abortControllerRef.current = new AbortController();
		try {
			const response = await callbacksRef.current.mutationFn(params);
			const stream = events(response, abortControllerRef.current.signal);
			for await (const event of stream) {
				if (event.event === 'Heartbeat') {
					continue;
				}
				setStatus('streaming');
				callbacksRef.current.onEvent(event, params);
			}
			setStatus('idle');
			callbacksRef.current.onSuccess?.(params);
		} catch (e) {
			const err = e as Error;
			setError(err);
			setStatus('error');
			callbacksRef.current.onError?.(err, params);
		}
	}, []);
	const isLoading = status === 'loading';
	const isStreaming = status === 'streaming';
	const isError = status === 'error';
	const reset = () => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		setStatus('idle');
		setError(null);
	};
	return { mutate, isLoading, isStreaming, isError, error, reset };
}
